import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { catchError, concatMap, map, of, switchMap } from 'rxjs';
import { RuleScheduleService } from '../../services/rule-schedule/rule-schedule.service';
import { RuleScheduleActions } from './rule-schedule.actions';
import { ToastService } from '../../services/toast/toast.service';
import { ToastType } from '../../models';

@Injectable()
export class RuleScheduleEffects {
    private actions$ = inject(Actions);
    private ruleScheduleService = inject(RuleScheduleService);
    private toastService = inject(ToastService);

    // -------------------------------------------------------------------
    // Schedules
    // -------------------------------------------------------------------

    loadSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.loadSchedules),
        switchMap(() =>
            this.ruleScheduleService.getSchedules().pipe(
                map(schedules => RuleScheduleActions.loadSchedulesSuccess({ schedules })),
                catchError(error => of(RuleScheduleActions.loadSchedulesFailure({ error }))),
            ),
        ),
    ));

    createSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.createSchedule),
        concatMap(({ request }) =>
            this.ruleScheduleService.createSchedule(request).pipe(
                map(schedule => RuleScheduleActions.createScheduleSuccess({ schedule })),
                catchError(error => {
                    this.toastService.showToast('Failed to create rule schedule', ToastType.ERROR);
                    return of(RuleScheduleActions.createScheduleFailure({ error }));
                }),
            ),
        ),
    ));

    updateSchedule$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.updateSchedule),
        concatMap(({ request }) =>
            this.ruleScheduleService.updateSchedule(request).pipe(
                map(schedule => RuleScheduleActions.updateScheduleSuccess({ schedule })),
                catchError(error => {
                    this.toastService.showToast('Failed to update rule schedule', ToastType.ERROR);
                    return of(RuleScheduleActions.updateScheduleFailure({ error }));
                }),
            ),
        ),
    ));

    deleteSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.deleteSchedules),
        concatMap(({ ids }) =>
            this.ruleScheduleService.deleteSchedules(ids).pipe(
                map((deleted) => {
                    const deletedIds = (deleted ?? []).map(schedule => schedule.id);
                    const removedIds = deletedIds.length > 0 ? deletedIds : ids;
                    return RuleScheduleActions.deleteSchedulesSuccess({ ids: removedIds });
                }),
                catchError(error => {
                    this.toastService.showToast('Failed to delete rule schedules', ToastType.ERROR);
                    return of(RuleScheduleActions.deleteSchedulesFailure({ error }));
                }),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Rule links
    // -------------------------------------------------------------------

    loadRuleSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.loadRuleSchedules),
        switchMap(({ ruleId }) =>
            this.ruleScheduleService.getSchedulesForRule(ruleId).pipe(
                map(schedules => RuleScheduleActions.loadRuleSchedulesSuccess({ ruleId, schedules })),
                catchError(error => of(RuleScheduleActions.loadRuleSchedulesFailure({ error }))),
            ),
        ),
    ));

    // -------------------------------------------------------------------
    // Link / unlink
    // -------------------------------------------------------------------

    linkSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.linkSchedules),
        concatMap(({ ruleIds, scheduleIds }) =>
            this.ruleScheduleService.link(ruleIds, scheduleIds).pipe(
                map(result => RuleScheduleActions.linkSchedulesSuccess({ result })),
                catchError(error => {
                    this.toastService.showToast('Failed to link rule schedules', ToastType.ERROR);
                    return of(RuleScheduleActions.linkSchedulesFailure({ error }));
                }),
            ),
        ),
    ));

    unlinkSchedules$ = createEffect(() => this.actions$.pipe(
        ofType(RuleScheduleActions.unlinkSchedules),
        concatMap(({ ruleIds, scheduleIds }) =>
            this.ruleScheduleService.unlink(ruleIds, scheduleIds).pipe(
                map(result => RuleScheduleActions.unlinkSchedulesSuccess({ result })),
                catchError(error => {
                    this.toastService.showToast('Failed to unlink rule schedules', ToastType.ERROR);
                    return of(RuleScheduleActions.unlinkSchedulesFailure({ error }));
                }),
            ),
        ),
    ));
}
